import React from "react";
import { usd, CAMP_DAYS, CAMP_DAY_LABELS } from "../../lib/api";
import { Users, Gift, Download, Search, CheckCircle2, XCircle, DollarSign, Clock, Trash2 } from "lucide-react";
import { Stat, RegStatusBadge, H1 } from "./AdminShared";

export function OverviewPanel({ stats }) {
  return (
    <div>
      <H1>Overview</H1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat icon={Users} label="Total Registrations" value={stats.total_registrations} />
        <Stat icon={CheckCircle2} label="Confirmed" value={stats.confirmed} />
        <Stat icon={Clock} label="Pending Payment" value={stats.pending} tone="amber" />
        <Stat icon={Gift} label="Jerseys Remaining" value={`${stats.gifts.remaining}/${stats.gifts.total}`} tone="gold" />
        <Stat icon={DollarSign} label="Confirmed Revenue" value={usd(stats.revenue)} />
        <Stat icon={DollarSign} label="Outstanding" value={usd(stats.outstanding)} tone="amber" />
        <Stat icon={XCircle} label="Cancelled" value={stats.cancelled} tone="red" />
        <Stat icon={Gift} label="Jerseys Awarded" value={stats.gifts.allocated} tone="gold" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        {["football", "goalkeeper", "meals"].map((k) => (
          <div key={k} className="rounded-2xl bg-white border border-[#E2EBF0] p-5">
            <div className="font-bold text-[#03281B] capitalize mb-3">{(k === "football" ? "soccer" : k)} by day</div>
            {CAMP_DAYS.map((d) => (
              <div key={d} className="flex justify-between text-sm py-1 border-b border-slate-50 last:border-0"><span className="text-slate-500">{CAMP_DAY_LABELS[d]}</span><span className="font-bold text-[#008057]">{stats.sessions_by_day[k][d]}</span></div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function RegistrationFilters({ q, setQ, statusFilter, setStatusFilter, refFilter, setRefFilter, onSearch }) {
  return (
    <div className="flex flex-wrap gap-3 mt-4">
      <div className="relative flex-1 min-w-[240px]">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSearch()} placeholder="Search name, email, or reference" className="w-full rounded-xl border-2 border-[#E2EBF0] pl-9 pr-3 py-2.5 text-sm focus:border-[#008057] outline-none" />
      </div>
      <select data-testid="admin-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-xl border-2 border-[#E2EBF0] px-3 py-2.5 text-sm">
        <option value="">All statuses</option><option value="pending_payment">Pending</option><option value="confirmed">Confirmed</option><option value="cancelled">Cancelled</option>
      </select>
      <input data-testid="admin-referral-filter" value={refFilter} onChange={(e) => setRefFilter(e.target.value.toUpperCase().trim())} placeholder="Referral code (or NONE)" className="rounded-xl border-2 border-[#E2EBF0] px-3 py-2.5 text-sm font-mono uppercase w-52" />
      <button data-testid="admin-search-btn" onClick={onSearch} className="rounded-xl bg-[#008057] text-white px-5 py-2.5 font-bold text-sm">Search</button>
    </div>
  );
}

function RegistrationRow({ o, onView, onDelete }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-4 py-3 font-mono font-semibold text-[#03281B]">{o.reference}</td>
      <td className="px-4 py-3">{o.guardian.first_name} {o.guardian.last_name}<div className="text-xs text-slate-400">{o.guardian.email}</div></td>
      <td className="px-4 py-3 hidden md:table-cell">{o.participants.length}</td>
      <td className="px-4 py-3 hidden lg:table-cell">{o.referral_code ? <span data-testid={`reg-referral-${o.reference}`} className="text-xs font-mono font-bold px-2 py-1 rounded-full bg-amber-50 text-[#B8860B]">{o.referral_code}</span> : <span className="text-slate-300">—</span>}</td>
      <td className="px-4 py-3 font-semibold">{usd(o.total)}</td>
      <td className="px-4 py-3"><RegStatusBadge status={o.registration_status} /></td>
      <td className="px-4 py-3 text-right"><div className="flex items-center justify-end gap-3"><button data-testid={`view-reg-${o.reference}`} onClick={() => onView(o)} className="text-[#008057] font-semibold hover:underline">View</button><button data-testid={`delete-reg-${o.reference}`} onClick={() => onDelete(o.reference)} title="Delete registration" className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button></div></td>
    </tr>
  );
}

export function RegistrationsPanel({ regs, filters, onSearch, onView, onDelete }) {
  return (
    <div>
      <H1>Registrations</H1>
      <RegistrationFilters {...filters} onSearch={onSearch} />
      <div className="mt-4 rounded-2xl bg-white border border-[#E2EBF0] overflow-x-auto">
        <table data-testid="admin-dashboard-table" className="w-full text-sm">
          <thead className="bg-[#F2F8F5] text-slate-600"><tr>
            <th className="text-left px-4 py-3 font-semibold">Reference</th><th className="text-left px-4 py-3 font-semibold">Guardian</th><th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Participants</th><th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Referral</th><th className="text-left px-4 py-3 font-semibold">Total</th><th className="text-left px-4 py-3 font-semibold">Status</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {regs.map((o) => <RegistrationRow key={o.id} o={o} onView={onView} onDelete={onDelete} />)}
            {regs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No registrations found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function GiftsPanel({ gifts }) {
  return (
    <div>
      <H1>Jersey Gifts</H1>
      <div className="grid grid-cols-3 gap-4 mt-6 max-w-lg">
        <Stat icon={Gift} label="Total" value={gifts.status.total} tone="gold" />
        <Stat icon={Gift} label="Awarded" value={gifts.status.allocated} tone="gold" />
        <Stat icon={Gift} label="Remaining" value={gifts.status.remaining} tone="gold" />
      </div>
      <div className="mt-6 rounded-2xl bg-white border border-[#E2EBF0] overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-[#F2F8F5] text-slate-600"><tr><th className="text-left px-4 py-3">#</th><th className="text-left px-4 py-3">Reference</th><th className="text-left px-4 py-3">Participant</th><th className="text-left px-4 py-3">Size</th><th className="text-left px-4 py-3">Confirmed</th></tr></thead>
          <tbody>{gifts.allocations.map((g) => (<tr key={g.reference} className="border-t border-slate-100"><td className="px-4 py-3 font-bold">{g.allocation_number}</td><td className="px-4 py-3 font-mono">{g.reference}</td><td className="px-4 py-3">{g.participant_name}</td><td className="px-4 py-3">{g.jersey_size || <span className="text-slate-400">Pending</span>}</td><td className="px-4 py-3 text-xs text-slate-400">{g.confirmed_at?.slice(0, 10)}</td></tr>))}
            {gifts.allocations.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No jerseys awarded yet.</td></tr>}</tbody></table>
      </div>
    </div>
  );
}

const EXPORTS = [["registrations", "Registrations CSV", "All registrations with guardian & totals"], ["sizes", "Clothing Sizes CSV", "Kit sizes per participant for equipment prep"], ["services", "Service Bookings CSV", "Additional services per participant"]];

export function ExportsPanel({ onExport }) {
  return (
    <div>
      <H1>Exports</H1>
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {EXPORTS.map(([kind, label, desc]) => (
          <button key={kind} data-testid={`export-${kind}`} onClick={() => onExport(kind)} className="rounded-2xl bg-white border border-[#E2EBF0] p-6 text-left hover:border-[#008057] transition-colors">
            <Download className="text-[#008057]" /><div className="font-bold text-[#03281B] mt-3">{label}</div><div className="text-sm text-slate-500 mt-1">{desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function AuditPanel({ audit }) {
  return (
    <div>
      <H1>Audit Log</H1>
      <div className="mt-6 rounded-2xl bg-white border border-[#E2EBF0] overflow-x-auto">
        <table data-testid="audit-table" className="w-full text-sm"><thead className="bg-[#F2F8F5] text-slate-600"><tr><th className="text-left px-4 py-3">Time</th><th className="text-left px-4 py-3">Action</th><th className="text-left px-4 py-3">Actor</th><th className="text-left px-4 py-3">Reference</th><th className="text-left px-4 py-3">Details</th></tr></thead>
          <tbody>{audit.map((l) => (<tr key={`${l.timestamp}-${l.action}-${l.reference}`} className="border-t border-slate-100"><td className="px-4 py-3 text-xs text-slate-400">{l.timestamp?.slice(0, 19).replace("T", " ")}</td><td className="px-4 py-3 font-semibold">{l.action}</td><td className="px-4 py-3">{l.actor}</td><td className="px-4 py-3 font-mono">{l.reference}</td><td className="px-4 py-3 text-slate-500">{l.details}</td></tr>))}
            {audit.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No audit entries yet.</td></tr>}</tbody></table>
      </div>
    </div>
  );
}
